// src/pages/Dashboard.js - User Dashboard
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { getUser } from '../utils/auth';
import { getErrorMessage } from '../utils/error';
import { useNotification } from '../hooks/useNotification';
import NotificationContainer from '../components/NotificationContainer';
import Navbar from '../components/common/Navbar';
import FriendlyAnalysis from '../components/common/FriendlyAnalysis';
import StreamingHateSpeechResponse from '../components/common/StreamingHateSpeechResponse';
import DisclaimerFooter from '../components/common/DisclaimerFooter';

// Parse [label](url) in text and return array of React nodes (text and <a>)
function parseMessageWithLinks(text) {
  if (!text || typeof text !== 'string') return text;
  const regex = /\[([^\]]+)\]\((https?:\S+)\)/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      React.createElement('a', {
        key: `link-${match.index}`,
        href: match[2],
        target: '_blank',
        rel: 'noopener noreferrer',
        className: 'text-shayek-primary dark:text-shayek-red-light hover:underline font-medium'
      }, match[1])
    );
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : text;
}

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('hate-speech');
  const [textInput, setTextInput] = useState('');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [error, setError] = useState('');
  
  // AI detection state
  const [aiDetectionFile, setAiDetectionFile] = useState(null);
  const [aiDetectionResult, setAiDetectionResult] = useState(null);
  const [aiDetectionLoading, setAiDetectionLoading] = useState(false);
  
  // Fact-check state
  const [factCheckText, setFactCheckText] = useState('');
  const [factCheckResult, setFactCheckResult] = useState(null);
  const [factCheckLoading, setFactCheckLoading] = useState(false);
  
  // Human rights report form
  const [hrReport, setHrReport] = useState({
    title: '',
    description: '',
    location: '',
    date: '',
    evidence_type: 'text',
    evidence_url: ''
  });
  const [hrImages, setHrImages] = useState([]);
  const [hrVideos, setHrVideos] = useState([]);
  const [hrDocuments, setHrDocuments] = useState([]);
  const [hrSubmitting, setHrSubmitting] = useState(false);
  const [hrSuccess, setHrSuccess] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  
  const user = getUser();
  const { t, i18n } = useTranslation();
  const { notifications, showSuccess, showError, showWarning, removeNotification } = useNotification();
  const hasLoadedProfileRef = useRef(false); // Prevent duplicate profile loads
  const hasLoadedHistoryRef = useRef(false); // Prevent report API loop - load once unless forced
  const isLoadingHistoryRef = useRef(false); // Prevent duplicate simultaneous report API calls
  const streamingWordIndexRef = useRef(0);
  const streamingIntervalRef = useRef(null);
  const streamingIntervalForIdRef = useRef(null);
  const aiDetectionFileInputRef = useRef(null);

  // Set document direction on language change
  useEffect(() => {
    const lng = localStorage.getItem('language') || 'en';
    document.documentElement.dir = lng === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lng;
  }, [i18n.language]);

  useEffect(() => {
    // Only load history once for admin/journalist (loadHistory sets hasLoadedHistoryRef on success/403)
    if (user && (user.role === 'admin' || user.role === 'journalist') && !hasLoadedHistoryRef.current) {
      loadHistory();
    }
    // Load user profile only once
    if (user && !hasLoadedProfileRef.current) {
      hasLoadedProfileRef.current = true;
      loadUserProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Removed navigate from dependencies

  const loadUserProfile = async () => {
    try {
      const response = await api.get('/api/profile/me');
      setUserProfile(response.data.user);
    } catch (err) {
      // Silently fail - profile is optional
      // Don't log 429 errors as they're rate limit issues, not real errors
      if (err.response?.status !== 429) {
        console.error('Failed to load profile:', err);
      }
    }
  };

  // Auto-scroll chat to bottom when new messages are added
  useEffect(() => {
    const chatContainer = document.getElementById('chat-messages');
    if (chatContainer) {
      chatContainer.scrollTop = chatContainer.scrollHeight;
    }
  }, [chatMessages, loading]);

  // Typewriter effect: advance streaming AI message word-by-word
  useEffect(() => {
    const streamingMsg = chatMessages.find(m => m.type === 'ai' && m.streaming);
    const msgId = streamingMsg && (streamingMsg.timestamp instanceof Date ? streamingMsg.timestamp.getTime() : streamingMsg.timestamp);

    if (!streamingMsg || !streamingMsg.contentToStream) {
      streamingWordIndexRef.current = 0;
      if (streamingIntervalRef.current) {
        clearInterval(streamingIntervalRef.current);
        streamingIntervalRef.current = null;
        streamingIntervalForIdRef.current = null;
      }
      return;
    }

    const fullText = streamingMsg.contentToStream;
    const words = fullText.split(/(\s+)/); // keep spaces

    const tick = () => {
      streamingWordIndexRef.current += 1;
      const nextContent = words.slice(0, streamingWordIndexRef.current).join('');
      const done = streamingWordIndexRef.current >= words.length;

      setChatMessages(prev => prev.map(m => {
        const mId = m.timestamp instanceof Date ? m.timestamp.getTime() : m.timestamp;
        if (m.type === 'ai' && mId === msgId) {
          return { ...m, content: nextContent, streaming: !done };
        }
        return m;
      }));

      if (done && streamingIntervalRef.current) {
        clearInterval(streamingIntervalRef.current);
        streamingIntervalRef.current = null;
        streamingIntervalForIdRef.current = null;
      }
    };

    // Only start interval once per streaming message
    if (words.length > 0 && streamingIntervalForIdRef.current !== msgId) {
      if (streamingIntervalRef.current) {
        clearInterval(streamingIntervalRef.current);
        streamingIntervalRef.current = null;
      }
      streamingIntervalForIdRef.current = msgId;
      streamingIntervalRef.current = setInterval(tick, 45);
    }

    return () => {
      // Clear interval only when this effect's streaming message is gone or changed
      if (streamingIntervalForIdRef.current !== msgId && streamingIntervalRef.current) {
        clearInterval(streamingIntervalRef.current);
        streamingIntervalRef.current = null;
        streamingIntervalForIdRef.current = null;
      }
    };
  }, [chatMessages]);

  // Reset streaming word index when a new streaming message is added (different message than current)
  const streamingMessageIdRef = useRef(null);
  useEffect(() => {
    const streamingMsg = chatMessages.find(m => m.type === 'ai' && m.streaming);
    if (streamingMsg) {
      const id = streamingMsg.timestamp instanceof Date ? streamingMsg.timestamp.getTime() : streamingMsg.timestamp;
      if (streamingMessageIdRef.current !== id) {
        streamingMessageIdRef.current = id;
        streamingWordIndexRef.current = 0;
      }
    } else {
      streamingMessageIdRef.current = null;
      streamingWordIndexRef.current = 0;
    }
  }, [chatMessages]);

  const loadHistory = async (force = false) => {
    // Prevent duplicate simultaneous calls
    if (isLoadingHistoryRef.current) {
      return;
    }
    // Regular users don't have access to reports endpoint - skip silently
    if (!user || (user.role !== 'admin' && user.role !== 'journalist')) {
      return;
    }
    // If already loaded and not forced, skip (prevent report API loop)
    if (!force && hasLoadedHistoryRef.current) {
      return;
    }
    isLoadingHistoryRef.current = true;
    try {
      const endpoint = user?.role === 'admin' ? '/moderate/reports?limit=10' : '/api/reports/reports?limit=10';
      const response = await api.get(endpoint);
      if (response.data.reports) {
        const list = user?.role === 'journalist'
          ? response.data.reports.filter(r => r.user_id === user?.id)
          : response.data.reports;
        setHistory(list);
        hasLoadedHistoryRef.current = true;
      }
    } catch (err) {
      // Silently fail for 403 (permission denied) - expected for regular users
      if (err.response?.status === 403) {
        hasLoadedHistoryRef.current = true; // Mark as attempted to prevent retry loop
        isLoadingHistoryRef.current = false;
        return;
      }
      console.error('Failed to load history:', err);
    } finally {
      isLoadingHistoryRef.current = false;
    }
  };

  // Hate Speech Recognition Chat Handler
  const handleHateSpeechSubmit = async (e) => {
    e.preventDefault();
    if (!textInput.trim()) return;

    const userMessage = textInput.trim();
    setTextInput('');
    setLoading(true);
    setError('');

    // Add user message to chat
    setChatMessages(prev => [...prev, {
      type: 'user',
      content: userMessage,
      timestamp: new Date()
    }]);

    try {
      const response = await api.post('/moderate/text', { text: userMessage });
      const result = response.data.result || response.data;

      const isArabic = result.language === 'ar' || /[\u0600-\u06FF]/.test(userMessage);
      const tForReply = isArabic ? i18n.getFixedT('ar') : i18n.getFixedT('en');

      const explanation = (result.explanation && typeof result.explanation === 'string' && result.explanation.trim())
        ? result.explanation.trim()
        : (isArabic ? 'تم تصنيف النص وفق أطر التحليل المعتمدة.' : 'The text was classified based on our analysis frameworks.');
      const refs = result.references && Array.isArray(result.references) ? result.references : [];
      const refLines = refs.map((r) => r.source || r.title || r.url).filter(Boolean);
      const confidencePct = result.confidence != null ? (Number(result.confidence) * 100).toFixed(1) : '—';
      const severity = result.severity ? String(result.severity) : 'N/A';
      const categories = Array.isArray(result.categories) ? result.categories : [];
      const catLabels = categories.slice(0, 5).map((c) => tForReply('report.categories.' + c, { defaultValue: String(c).replace(/_/g, ' ') }));
      const keywords = Array.isArray(result.keywords) ? result.keywords : [];
      const patterns = Array.isArray(result.detected_patterns) ? result.detected_patterns : [];

      const whyLabel = tForReply('dashboard.whyWeConcluded', 'Why we concluded this:');
      const basedOnLabel = tForReply('common.basedOn', 'Based on') + ' / ' + tForReply('common.references', 'References');
      const confidenceLabel = isArabic ? `أنا واثق من هذا التحليل بنسبة ${confidencePct}٪.` : `I am ${confidencePct}% confident of this analysis`;
      const severityLabel = 'Severity Level';
      const categoriesLabel = tForReply('journalist.summarize.detectedCategories', 'Detected Categories');
      const triggerLabel = 'Trigger Words/Flags';
      const patternsLabel = tForReply('common.detectedPatterns', 'Detected Patterns');

      // Build intro + explanatory block to be more descriptive but still concise
      let introText;
      if (result.is_hate_speech) {
        const catLabel = catLabels[0] || tForReply('report.categories.hate_speech', 'Hate Speech');
        introText = isArabic
          ? `تحليل النص الذي أرسلته يشير إلى وجود خطاب كراهية (${catLabel}). هذا تحليل آلي أولي يوضح لك كيف صنّف النظام هذا النص وخطورته المحتملة.`
          : `I've analyzed the text you provided and concluded that ${catLabel} was detected. Here is a breakdown of how the system reached this conclusion and what it means.`;
      } else {
        introText = isArabic
          ? 'تحليل النص الذي أرسلته يشير إلى عدم وجود خطاب كراهية. يعرض لك التحليل أدناه سبب هذا التقييم وما الذي راقبه النظام في النص.'
          : "I've analyzed the text you provided and concluded that no hate speech was detected. The breakdown below explains why the system reached this assessment and what signals it looked at.";
      }

      // If explanation is very short, append a brief, informative suffix about frameworks + human review
      const explanationIsShort = explanation.length < 260;
      const whySuffix = explanationIsShort
        ? (isArabic
            ? 'يعتمد هذا التقييم على أنماط موثقة لخطاب الكراهية وإرشادات متعددة اللغات خاصة بفريق شييك. يمكنك دائماً طلب مراجعة بشرية إذا شعرت أن التحليل لا يعكس السياق بدقة.'
            : 'This assessment relies on documented hate-speech patterns and multilingual guidelines curated by the Chayyek team. You can always request human review if you feel the analysis does not fully reflect the context.')
        : (isArabic
            ? 'يمكنك طلب مراجعة بشرية إذا شعرت أن التحليل لا يعكس السياق بدقة.'
            : 'If you disagree with this assessment or feel the context is more nuanced, you can request a human review from the Chayyek team.');

      const explanationBlock = `${explanation}\n\n${whySuffix}`;

      const closingLine = isArabic
        ? 'هذا التحليل يتم بواسطة الذكاء الاصطناعي لأغراض الإرشاد؛ في الحالات الحساسة أو المختلف عليها يمكنك رفع البلاغ لمراجعة فريق شييك البشري.'
        : 'This analysis is generated by AI for guidance; for sensitive or disputed cases, you can submit this content for human review by the Chayyek team.';

      const sectionRanges = [];
      let pos = 0;
      const push = (text, type) => {
        if (!text) return;
        const start = pos;
        pos += text.length;
        sectionRanges.push({ start, end: pos, type });
      };

      push(introText + '\n\n', 'intro');
      push(whyLabel + '\n', 'why');
      push(explanationBlock + '\n\n', 'why');
      push(basedOnLabel + '\n', 'references');
      push(refLines.join('\n') + (refLines.length ? '\n\n' : ''), 'references');
      push(confidenceLabel + '\n\n', 'confidence');
      push(severityLabel + '\n' + severity + '\n\n', 'severity');
      push(categoriesLabel + '\n' + catLabels.join('\n') + (catLabels.length ? '\n\n' : ''), 'categories');
      if (keywords.length > 0) {
        push(triggerLabel + '\n' + keywords.join('\n') + '\n\n', 'keywords');
      }
      push(patternsLabel + '\n' + patterns.join('\n') + '\n\n', 'patterns');
      push(closingLine, 'closing');

      const triggerBlock = keywords.length > 0 ? (triggerLabel + '\n' + keywords.join('\n') + '\n\n') : '';
      const contentToStream =
        introText + '\n\n' +
        whyLabel + '\n' + explanationBlock + '\n\n' +
        basedOnLabel + '\n' + refLines.join('\n') + (refLines.length ? '\n\n' : '') +
        confidenceLabel + '\n\n' +
        severityLabel + '\n' + severity + '\n\n' +
        categoriesLabel + '\n' + catLabels.join('\n') + (catLabels.length ? '\n\n' : '') +
        triggerBlock +
        patternsLabel + '\n' + patterns.join('\n') + '\n\n' +
        closingLine;

      // Add AI response to chat (full content streams with typewriter; StreamingHateSpeechResponse applies colors)
      setChatMessages(prev => [...prev, {
        type: 'ai',
        content: '',
        contentToStream,
        sectionRanges,
        result: result,
        timestamp: new Date(),
        streaming: true
      }]);
      
      showSuccess(t('dashboard.hateSpeech.analyzed') || 'Text analyzed successfully!');
      if (user && (user.role === 'admin' || user.role === 'journalist')) {
        loadHistory(true); // Force refresh after new analysis
      }
    } catch (err) {
      const status = err?.response?.status;
      const failClosed = status === 503 && (err?.response?.data?.fail_closed || err?.response?.data?.action === 'blocked_pending_review');

      if (failClosed) {
        const isArabic = /[\u0600-\u06FF]/.test(userMessage);
        const result = err?.response?.data?.result;
        const msg = isArabic
          ? `⚠️ تعذّر إجراء التحليل حالياً.\n\nلأسباب تتعلق بالسلامة، لا يمكننا اعتبار هذا النص آمناً الآن. تم حظر المحتوى مؤقتاً وتمت إضافته إلى قائمة المراجعة البشرية (pending review). حاول مرة أخرى لاحقاً.`
          : `⚠️ Analysis temporarily unavailable.\n\nFor safety, we can’t clear this text right now. It’s temporarily blocked and queued for human review (pending review). Please try again later.`;

        setChatMessages(prev => [...prev, {
          type: 'ai',
          content: msg,
          result,
          timestamp: new Date()
        }]);

        showWarning(t('dashboard.hateSpeech.unavailable') || 'AI temporarily unavailable — blocked pending review');
        return;
      }

      const errorMsg = getErrorMessage(
        err,
        t('dashboard.hateSpeech.error') || 'Failed to analyze text'
      );
      setError(errorMsg);
      showError(errorMsg);
      setChatMessages(prev => [...prev, {
        type: 'error',
        content: `❌ Error: ${errorMsg}`,
        timestamp: new Date()
      }]);
    } finally {
      setLoading(false);
    }
  };
  
  // AI Detection Handler - Checks if image/video is AI-generated or manipulated
  const handleAiDetection = async (e) => {
    e.preventDefault();
    if (!aiDetectionFile) return;

    setAiDetectionLoading(true);
    setAiDetectionResult(null);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', aiDetectionFile);
      
      // Add file type hint for better processing
      const isVideo = aiDetectionFile.type.startsWith('video/');
      const isImage = aiDetectionFile.type.startsWith('image/');
      if (isVideo) formData.append('file_type', 'video');
      if (isImage) formData.append('file_type', 'image');

      const response = await api.post('/moderate/image', formData, {
        timeout: isVideo ? 120000 : 60000, // Longer timeout for videos
      });
      
      const result = response.data.result || response.data;
      setAiDetectionResult(result);
      
      if (result.is_ai_manipulated) {
        showError(t('dashboard.deepfake.detected') || 'AI manipulation detected! This content may be generated or altered.');
      } else {
        showSuccess(t('dashboard.deepfake.notDetected') || 'No AI manipulation detected. Content appears authentic.');
      }
    } catch (err) {
      const errorMsg = getErrorMessage(
        err,
        t('dashboard.deepfake.error') || 'Failed to analyze file'
      );
      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setAiDetectionLoading(false);
    }
  };
  
  // Fact-Check Handler
  const handleFactCheck = async (e) => {
    e.preventDefault();
    if (!factCheckText.trim()) return;

    setFactCheckLoading(true);
    setFactCheckResult(null);
    setError('');

    try {
      const response = await api.post('/api/reports/fact-check', {
        text: factCheckText.trim()
      });
      
      const result = response.data.fact_check || response.data;
      setFactCheckResult(result);
      
      if (result.verified) {
        showSuccess(t('dashboard.factCheck.verified') || 'Information verified against news sources.');
      } else {
        showError(t('dashboard.factCheck.notVerified') || 'Could not verify this information against known sources.');
      }
    } catch (err) {
      const errorMsg = getErrorMessage(
        err,
        t('dashboard.factCheck.error') || 'Failed to fact-check text'
      );
      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setFactCheckLoading(false);
    }
  };


  const handleHrSubmit = async (e) => {
    e.preventDefault();
    if (!hrReport.title || !hrReport.description) return;

    setHrSubmitting(true);
    setHrSuccess(false);
    setError('');

    try {
      const formData = new FormData();
      formData.append('title', hrReport.title);
      formData.append('description', hrReport.description);
      formData.append('location', hrReport.location);
      formData.append('date', hrReport.date);
      formData.append('evidence_type', hrReport.evidence_type);
      formData.append('evidence_url', hrReport.evidence_url);

      // Append images
      hrImages.forEach((file, index) => {
        formData.append('images', file);
      });

      // Append videos
      hrVideos.forEach((file, index) => {
        formData.append('videos', file);
      });

      // Append documents
      hrDocuments.forEach((file, index) => {
        formData.append('documents', file);
      });

      await api.post('/moderate/human-rights', formData, {});
      
      setHrSuccess(true);
      showSuccess('Human rights violation report submitted successfully! Administrators will review it.');
      setHrReport({
        title: '',
        description: '',
        location: '',
        date: '',
        evidence_type: 'text',
        evidence_url: ''
      });
      setHrImages([]);
      setHrVideos([]);
      setHrDocuments([]);
      setTimeout(() => setHrSuccess(false), 5000);
      if (user && (user.role === 'admin' || user.role === 'journalist')) {
        loadHistory(true); // Force refresh after new report
      }
    } catch (err) {
      const errorMsg = getErrorMessage(err, 'Failed to submit report');
      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setHrSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-shayek-light-50 to-shayek-light-100 dark:from-shayek-dark dark:to-shayek-dark-50 transition-colors">
      <Navbar showHome={true} />
      <NotificationContainer 
        notifications={notifications} 
        onRemove={removeNotification} 
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek mb-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
          <div className="border-b border-shayek-light-300 dark:border-shayek-dark-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('hate-speech')}
                className={`px-6 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'hate-speech'
                    ? 'border-b-2 border-shayek-primary dark:border-shayek-red-light text-shayek-primary dark:text-shayek-red-light'
                    : 'text-shayek-light-500 dark:text-shayek-dark-500 hover:text-shayek-light-700 dark:hover:text-shayek-dark-700'
                }`}
              >
                {t('dashboard.tabs.hateSpeech') || 'Hate Speech Recognition'}
              </button>
              <button
                onClick={() => setActiveTab('ai-detection')}
                className={`px-6 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'ai-detection'
                    ? 'border-b-2 border-shayek-primary dark:border-shayek-red-light text-shayek-primary dark:text-shayek-red-light'
                    : 'text-shayek-light-500 dark:text-shayek-dark-500 hover:text-shayek-light-700 dark:hover:text-shayek-dark-700'
                }`}
              >
                {t('dashboard.tabs.deepfake') || 'AI Detection'}
                <span className="ml-1.5 px-1.5 py-0.5 text-xs font-semibold rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-600">
                  {t('common.beta') || 'Beta'}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('fact-check')}
                className={`px-6 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'fact-check'
                    ? 'border-b-2 border-shayek-primary dark:border-shayek-red-light text-shayek-primary dark:text-shayek-red-light'
                    : 'text-shayek-light-500 dark:text-shayek-dark-500 hover:text-shayek-light-700 dark:hover:text-shayek-dark-700'
                }`}
              >
                {t('dashboard.tabs.factCheck') || 'Fact Check'}
              </button>
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Hate Speech Recognition Tab */}
            {activeTab === 'hate-speech' && (
              <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-semibold text-shayek-light-900 dark:text-shayek-dark-900">Hate Speech Recognition</h2>
                    <p className="text-sm text-shayek-light-600 dark:text-shayek-dark-600 mt-1">
                      Chat with our AI to analyze text for hate speech. Get instant feedback and explanations.
                    </p>
                  </div>
                  {chatMessages.length > 0 && (
                    <button
                      onClick={() => {
                        setChatMessages([]);
                        setTextInput('');
                      }}
                      className="px-3 py-1.5 text-xs text-shayek-light-600 dark:text-shayek-dark-600 hover:text-shayek-light-900 dark:hover:text-shayek-dark-900 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg hover:bg-shayek-light-50 dark:hover:bg-shayek-dark-50 transition-colors"
                      title="Clear chat"
                    >
                      Clear
                    </button>
                  )}
                </div>
                
                {/* Chat Messages */}
                <div 
                  id="chat-messages" 
                  className="mb-4 h-96 overflow-y-auto border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg p-4 bg-shayek-light-50 dark:bg-shayek-dark-50 space-y-4 transition-colors scroll-smooth"
                >
                  {chatMessages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full py-12">
                      <div className="w-16 h-16 bg-shayek-primary/10 dark:bg-shayek-primary/20 rounded-full flex items-center justify-center mb-4">
                        <svg className="w-8 h-8 text-shayek-primary dark:text-shayek-red-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      </div>
                      <p className="text-shayek-light-500 dark:text-shayek-dark-500 text-center max-w-sm">
                        {t('dashboard.hateSpeech.noMessages') || 'Start a conversation by entering text to analyze for hate speech'}
                      </p>
                      <p className="text-xs text-shayek-light-400 dark:text-shayek-dark-400 mt-2 text-center">
                        Try pasting a text snippet to see how our AI analyzes it
                      </p>
                    </div>
                  ) : (
                    chatMessages.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`group flex gap-3 ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        {/* Avatar */}
                        {msg.type !== 'user' && (
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-shayek-primary/10 dark:bg-shayek-primary/20 flex items-center justify-center">
                            <svg className="w-4 h-4 text-shayek-primary dark:text-shayek-red-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                          </div>
                        )}
                        
                        <div className={`flex flex-col ${msg.type === 'user' ? 'items-end' : 'items-start'} max-w-[75%]`}>
                          <div
                            className={`rounded-lg p-4 ${
                              msg.type === 'user'
                                ? 'bg-shayek-primary dark:bg-shayek-primary-600 text-white rounded-br-none'
                                : msg.type === 'error'
                                ? 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-400 border border-red-200 dark:border-red-800'
                                : 'bg-white dark:bg-shayek-dark-100 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-bl-none shadow-sm'
                            }`}
                          >
                            {msg.type === 'user' ? (
                              <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{msg.content}</p>
                            ) : msg.result && msg.sectionRanges ? (
                              <StreamingHateSpeechResponse
                                visibleText={msg.streaming ? msg.content : (msg.content || msg.contentToStream || '')}
                                result={msg.result}
                                sectionRanges={msg.sectionRanges}
                                isStreaming={!!msg.streaming}
                              />
                            ) : (
                              <div className="text-sm whitespace-pre-line leading-relaxed text-shayek-light-900 dark:text-shayek-dark-900">
                                {msg.streaming ? msg.content : parseMessageWithLinks(msg.content || msg.contentToStream || '')}
                                {msg.streaming && <span className="inline-block w-2 h-4 ml-0.5 bg-shayek-primary dark:bg-shayek-red-light animate-pulse align-middle" aria-hidden="true" />}
                              </div>
                            )}
                          </div>
                          
                          {/* Timestamp and actions */}
                          <div className={`flex items-center gap-2 mt-1 ${msg.type === 'user' ? 'flex-row-reverse' : ''}`}>
                            <span className="text-xs text-shayek-light-400 dark:text-shayek-dark-400">
                              {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            {msg.type !== 'user' && (
                              <button
                                onClick={() => {
                                  const textToCopy = msg.contentToStream ?? msg.content;
                                  navigator.clipboard.writeText(textToCopy);
                                  showSuccess('Message copied to clipboard!');
                                }}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 hover:bg-shayek-light-100 dark:hover:bg-shayek-dark-200 rounded text-shayek-light-500 dark:text-shayek-dark-500 hover:text-shayek-light-700 dark:hover:text-shayek-dark-700"
                                title="Copy message"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                        
                        {/* User Avatar */}
                        {msg.type === 'user' && (
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-shayek-primary/20 dark:bg-shayek-primary/30 flex items-center justify-center">
                            <svg className="w-4 h-4 text-shayek-primary dark:text-shayek-red-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                  {loading && (
                    <div className="flex justify-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-shayek-primary/10 dark:bg-shayek-primary/20 flex items-center justify-center">
                        <svg className="w-4 h-4 text-shayek-primary dark:text-shayek-red-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                      </div>
                      <div className="bg-white dark:bg-shayek-dark-100 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg rounded-bl-none p-4 shadow-sm">
                        <div className="flex items-center gap-2">
                          <div className="flex space-x-1.5">
                            <div className="w-2 h-2 bg-shayek-primary dark:bg-shayek-red-light rounded-full animate-bounce"></div>
                            <div className="w-2 h-2 bg-shayek-primary dark:bg-shayek-red-light rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                            <div className="w-2 h-2 bg-shayek-primary dark:bg-shayek-red-light rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                          </div>
                          <span className="text-xs text-shayek-light-500 dark:text-shayek-dark-500">{t('dashboard.hateSpeech.thinking', 'Chayyek is thinking...')}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Input Form */}
                <form onSubmit={handleHateSpeechSubmit} className="space-y-3">
                  <div className="relative">
                    <textarea
                      value={textInput}
                      onChange={(e) => setTextInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                          e.preventDefault();
                          if (!loading && textInput.trim()) {
                            handleHateSpeechSubmit(e);
                          }
                        }
                      }}
                      placeholder={t('dashboard.hateSpeech.placeholder') || 'Type or paste text to analyze... (Press Ctrl+Enter to send)'}
                      rows={3}
                      className="w-full px-4 py-3 pr-12 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 focus:ring-2 focus:ring-shayek-primary focus:border-transparent transition-colors resize-none"
                    />
                    <div className="absolute bottom-2 right-2 text-xs text-shayek-light-400 dark:text-shayek-dark-400">
                      {textInput.length > 0 && `${textInput.length} characters`}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="submit"
                      disabled={loading || !textInput.trim()}
                      className="flex-1 bg-shayek-primary dark:bg-shayek-primary-600 text-white py-3 px-6 rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-shayek font-medium flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>{t('dashboard.hateSpeech.analyzing') || 'Analyzing...'}</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                          </svg>
                          <span>{t('dashboard.hateSpeech.sendButton') || 'Analyze Text'}</span>
                        </>
                      )}
                    </button>
                    {textInput.trim() && (
                      <button
                        type="button"
                        onClick={() => setTextInput('')}
                        className="px-4 py-3 text-shayek-light-600 dark:text-shayek-dark-600 hover:text-shayek-light-900 dark:hover:text-shayek-dark-900 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg hover:bg-shayek-light-50 dark:hover:bg-shayek-dark-50 transition-colors"
                        title="Clear input"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                </form>
              </div>
            )}

            {/* AI Detection Tab */}
            {activeTab === 'ai-detection' && (
              <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
                <h2 className="text-xl font-semibold mb-4 text-shayek-light-900 dark:text-shayek-dark-900 flex items-center gap-2">
                  {t('dashboard.deepfake.title') || 'AI Detection'}
                  <span className="px-2 py-0.5 text-xs font-semibold rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-600">
                    {t('common.beta') || 'Beta'}
                  </span>
                </h2>
                <p className="text-sm text-shayek-light-600 dark:text-shayek-dark-600 mb-6">
                  {t('dashboard.deepfake.description') || 'Upload an image or video to check if it was created or manipulated by AI. Our AI will analyze the content for signs of manipulation.'}
                </p>

                {error && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                    {error}
                  </div>
                )}

                <form onSubmit={handleAiDetection} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">
                      {t('dashboard.deepfake.fileLabel') || 'Upload Image or Video'} <span className="text-red-500">*</span>
                    </label>
                    <input
                      ref={aiDetectionFileInputRef}
                      type="file"
                      accept="image/*,video/*"
                      onChange={(e) => setAiDetectionFile(e.target.files[0])}
                      className="w-full px-4 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900"
                      required
                    />
                    {aiDetectionFile && (
                      <div className="mt-2 p-3 bg-shayek-light-50 dark:bg-shayek-dark-50 rounded-lg border border-shayek-light-300 dark:border-shayek-dark-200">
                        <p className="text-sm text-shayek-light-600 dark:text-shayek-dark-600">Selected: {aiDetectionFile.name}</p>
                        <p className="text-xs text-shayek-light-500 dark:text-shayek-dark-500">Size: {(aiDetectionFile.size / (1024 * 1024)).toFixed(2)} MB</p>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      type="submit"
                      disabled={aiDetectionLoading || !aiDetectionFile}
                      className="flex-1 bg-shayek-primary dark:bg-shayek-primary-600 text-white py-2 px-4 rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-shayek"
                    >
                      {aiDetectionLoading ? (t('dashboard.deepfake.analyzing') || 'Analyzing...') : (t('dashboard.deepfake.detectButton') || 'Check Authenticity')}
                    </button>
                    {(aiDetectionFile || aiDetectionResult) && (
                      <button
                        type="button"
                        disabled={aiDetectionLoading}
                        onClick={() => {
                          setAiDetectionFile(null);
                          setAiDetectionResult(null);
                          if (aiDetectionFileInputRef.current) {
                            aiDetectionFileInputRef.current.value = '';
                          }
                        }}
                        className="px-4 py-2 text-sm text-shayek-light-600 dark:text-shayek-dark-600 hover:text-shayek-light-900 dark:hover:text-shayek-dark-900 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg hover:bg-shayek-light-50 dark:hover:bg-shayek-dark-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={t('dashboard.deepfake.clear') || 'Clear'}
                      >
                        {t('dashboard.deepfake.clear') || 'Clear'}
                      </button>
                    )}
                  </div>
                </form>

                {aiDetectionResult && (() => {
                  const confidencePct = aiDetectionResult.confidence != null ? Math.round(aiDetectionResult.confidence * 100) : 0;
                  const isManipulated = aiDetectionResult.is_ai_manipulated === true;
                  const isUncertain = aiDetectionResult.is_uncertain === true;
                  const isRed = isManipulated;
                  const isAmber = !isManipulated && isUncertain;
                  const isGreen = !isManipulated && !isUncertain;
                  const containerClass = isRed ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-800' : isAmber ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-800' : 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-800';
                  const titleClass = isRed ? 'text-red-900 dark:text-red-300' : isAmber ? 'text-amber-900 dark:text-amber-300' : 'text-green-900 dark:text-green-300';
                  const bodyClass = isRed ? 'text-red-800 dark:text-red-200' : isAmber ? 'text-amber-800 dark:text-amber-200' : 'text-green-800 dark:text-green-200';
                  const borderClass = isRed ? 'border-red-200 dark:border-red-800' : isAmber ? 'border-amber-200 dark:border-amber-800' : 'border-green-200 dark:border-green-800';
                  const titleText = isRed ? `🚨 ${t('dashboard.deepfake.mightBeManipulated') || 'This might be AI manipulated or generated.'}` : isAmber ? (t('dashboard.deepfake.inconclusive') || 'Result inconclusive.') : `✅ ${t('dashboard.deepfake.notManipulatedPercent', { percent: confidencePct }) || `We are ${confidencePct}% sure this is not manipulated.`}`;
                  const subtitleText = isAmber ? (t('dashboard.deepfake.inconclusiveRecommendation') || 'We recommend manual review.') : null;
                  return (
                  <div className={`mt-6 p-4 rounded-lg border-2 ${containerClass}`}>
                    <h3 className={`font-semibold mb-2 ${titleClass}`}>
                      {titleText}
                    </h3>
                    {subtitleText && <p className={`text-sm mb-2 ${bodyClass}`}>{subtitleText}</p>}
                    <div className={`space-y-2 text-sm ${bodyClass}`}>
                      <div>
                        <strong className="text-shayek-light-900 dark:text-shayek-dark-900">Confidence:</strong> <span className="text-shayek-light-900 dark:text-shayek-dark-900">{confidencePct}%</span>
                      </div>
                      {aiDetectionResult.explanation && (
                        <div>
                          <strong className="text-shayek-light-900 dark:text-shayek-dark-900">
                            {t('dashboard.whyWeConcluded', 'Why we concluded this:')}
                          </strong>
                          <p className="mt-1 text-sm text-shayek-light-800 dark:text-shayek-dark-700">
                            {aiDetectionResult.explanation}
                          </p>
                        </div>
                      )}
                      {Array.isArray(aiDetectionResult.reasons) && aiDetectionResult.reasons.length > 0 && (
                        <div>
                          <ul className="list-disc list-inside space-y-1 text-shayek-light-800 dark:text-shayek-dark-700">
                            {aiDetectionResult.reasons.map((reason, idx) => (
                              <li key={idx}>{reason}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {aiDetectionResult.heatmap_base64 && (
                        <div className="mt-3">
                          <strong className="text-shayek-light-900 dark:text-shayek-dark-900">
                            {t('dashboard.deepfake.whereModelLooked', 'Where the model focused:')}
                          </strong>
                          <div className="mt-2 rounded overflow-hidden border border-shayek-light-200 dark:border-shayek-dark-300 bg-white dark:bg-shayek-dark-50">
                            <img
                              src={`data:image/png;base64,${aiDetectionResult.heatmap_base64}`}
                              alt="AI analysis heatmap"
                              className="w-full h-auto"
                            />
                          </div>
                        </div>
                      )}
                      <div className={`mt-3 p-3 bg-white dark:bg-shayek-dark-50 rounded border ${borderClass}`}>
                        <p className="text-sm text-shayek-light-700 dark:text-shayek-dark-700">
                          {t('dashboard.deepfake.betaDisclaimer') || 'Because this tool is in beta, if you have any doubts please check with Team Chayyek. You can also submit a report to Team Chayyek.'}
                        </p>
                      </div>
                    </div>
                  </div>
                  );
                })()}
              </div>
            )}

            {/* History Tab - Keep for reference but hide from main tabs */}
            {activeTab === 'history' && (
              <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
                <h2 className="text-xl font-semibold mb-4">Analysis History</h2>
                {history.length === 0 ? (
                  <p className="text-shayek-light-500 dark:text-shayek-dark-500">No history yet</p>
                ) : (
                  <div className="space-y-4">
                    {history.map((item) => (
                      <div key={item.id} className="border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg p-4 bg-white dark:bg-shayek-dark-100 transition-colors">
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700">
                            {item.input_type}
                          </span>
                          <span className="text-xs text-shayek-light-500 dark:text-shayek-dark-500">
                            {new Date(item.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-sm text-shayek-light-600 dark:text-shayek-dark-600 mb-2">{item.input_preview}</p>
                        {item.result_json && (
                          <div className="mt-2 p-3 bg-shayek-light-50 dark:bg-shayek-dark-50 rounded border border-shayek-light-300 dark:border-shayek-dark-200">
                            <FriendlyAnalysis data={item.result_json} />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Legacy Human Rights Tab - Keep for backward compatibility */}
            {activeTab === 'human-rights' && (
              <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
                <h2 className="text-xl font-semibold mb-4">Human Rights Violation Report</h2>
                <p className="text-sm text-shayek-light-600 dark:text-shayek-dark-600 mb-6">
                  Submit a report of human rights violations. This will be reviewed by administrators.
                </p>
                
                {hrSuccess && (
                  <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
                    Report submitted successfully! Administrators will review it.
                  </div>
                )}

                {error && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                    {error}
                  </div>
                )}

                <form onSubmit={handleHrSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={hrReport.title}
                      onChange={(e) => setHrReport({ ...hrReport, title: e.target.value })}
                      required
                      className="w-full px-4 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 focus:ring-2 focus:ring-shayek-primary transition-colors"
                      placeholder="Brief title of the violation"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">
                      Description *
                    </label>
                    <textarea
                      value={hrReport.description}
                      onChange={(e) => setHrReport({ ...hrReport, description: e.target.value })}
                      required
                      rows={6}
                      className="w-full px-4 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 focus:ring-2 focus:ring-shayek-primary transition-colors"
                      placeholder="Detailed description of the human rights violation..."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">
                        Location
                      </label>
                      <input
                        type="text"
                        value={hrReport.location}
                        onChange={(e) => setHrReport({ ...hrReport, location: e.target.value })}
                        className="w-full px-4 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 focus:ring-2 focus:ring-shayek-primary transition-colors"
                        placeholder="City, Country"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">
                        Date
                      </label>
                      <input
                        type="date"
                        value={hrReport.date}
                        onChange={(e) => setHrReport({ ...hrReport, date: e.target.value })}
                        className="w-full px-4 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 focus:ring-2 focus:ring-shayek-primary transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">
                      Evidence Type
                    </label>
                    <select
                      value={hrReport.evidence_type}
                      onChange={(e) => setHrReport({ ...hrReport, evidence_type: e.target.value })}
                      className="w-full px-4 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 focus:ring-2 focus:ring-shayek-primary transition-colors"
                    >
                      <option value="text">Text/Description</option>
                      <option value="image">Image</option>
                      <option value="video">Video</option>
                      <option value="document">Document</option>
                      <option value="link">Link/URL</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">
                      Evidence URL (if applicable)
                    </label>
                    <input
                      type="url"
                      value={hrReport.evidence_url}
                      onChange={(e) => setHrReport({ ...hrReport, evidence_url: e.target.value })}
                      className="w-full px-4 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 focus:ring-2 focus:ring-shayek-primary transition-colors"
                      placeholder="https://..."
                    />
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">
                        Image Attachments (Max 10, JPG, PNG, GIF, WebP)
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => setHrImages(Array.from(e.target.files))}
                        className="w-full px-4 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 focus:ring-2 focus:ring-shayek-primary transition-colors"
                      />
                      {hrImages.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {hrImages.map((file, idx) => (
                            <div key={idx} className="text-xs text-shayek-light-600 dark:text-shayek-dark-600 flex items-center justify-between bg-shayek-light-50 dark:bg-shayek-dark-50 p-2 rounded border border-shayek-light-300 dark:border-shayek-dark-200">
                              <span>{file.name}</span>
                              <span className="text-gray-400">{(file.size / 1024).toFixed(1)} KB</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">
                        Video Attachments (Max 5, MP4, MOV, AVI)
                      </label>
                      <input
                        type="file"
                        accept="video/*"
                        multiple
                        onChange={(e) => setHrVideos(Array.from(e.target.files))}
                        className="w-full px-4 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 focus:ring-2 focus:ring-shayek-primary transition-colors"
                      />
                      {hrVideos.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {hrVideos.map((file, idx) => (
                            <div key={idx} className="text-xs text-shayek-light-600 dark:text-shayek-dark-600 flex items-center justify-between bg-shayek-light-50 dark:bg-shayek-dark-50 p-2 rounded border border-shayek-light-300 dark:border-shayek-dark-200">
                              <span>{file.name}</span>
                              <span className="text-gray-400">{(file.size / (1024 * 1024)).toFixed(2)} MB</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">
                        Document Attachments (Max 5, PDF, DOC, DOCX)
                      </label>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        multiple
                        onChange={(e) => setHrDocuments(Array.from(e.target.files))}
                        className="w-full px-4 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 focus:ring-2 focus:ring-shayek-primary transition-colors"
                      />
                      {hrDocuments.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {hrDocuments.map((file, idx) => (
                            <div key={idx} className="text-xs text-shayek-light-600 dark:text-shayek-dark-600 flex items-center justify-between bg-shayek-light-50 dark:bg-shayek-dark-50 p-2 rounded border border-shayek-light-300 dark:border-shayek-dark-200">
                              <span>{file.name}</span>
                              <span className="text-gray-400">{(file.size / 1024).toFixed(1)} KB</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={hrSubmitting || !hrReport.title || !hrReport.description}
                    className="w-full bg-shayek-primary dark:bg-shayek-primary-600 text-white py-2 px-4 rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-light disabled:opacity-50 disabled:cursor-not-allowed shadow-shayek transition-colors"
                  >
                    {hrSubmitting ? 'Submitting...' : 'Submit Report'}
                  </button>
                </form>
              </div>
            )}

            {activeTab === 'history' && (
              <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
                <h2 className="text-xl font-semibold mb-4">Analysis History</h2>
                {history.length === 0 ? (
                  <p className="text-shayek-light-500 dark:text-shayek-dark-500">No history yet</p>
                ) : (
                  <div className="space-y-4">
                    {history.map((item) => (
                      <div key={item.id} className="border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg p-4 bg-white dark:bg-shayek-dark-100 transition-colors">
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700">
                            {item.input_type}
                          </span>
                          <span className="text-xs text-shayek-light-500 dark:text-shayek-dark-500">
                            {new Date(item.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-sm text-shayek-light-600 dark:text-shayek-dark-600 mb-2">{item.input_preview}</p>
                        {item.result_json && (
                          <div className="mt-2 p-3 bg-shayek-light-50 dark:bg-shayek-dark-50 rounded border border-shayek-light-300 dark:border-shayek-dark-200">
                            <FriendlyAnalysis data={item.result_json} />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Fact-Check Tab */}
            {activeTab === 'fact-check' && (
              <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
                <h2 className="text-xl font-semibold mb-4 text-shayek-light-900 dark:text-shayek-dark-900">{t('dashboard.factCheck.title') || 'Fact Check'}</h2>
                <p className="text-sm text-shayek-light-600 dark:text-shayek-dark-600 mb-6">
                  {t('dashboard.factCheck.description') || 'Enter text to verify against news articles and trusted sources. Our system will search for matching information and provide verification results.'}
                </p>

                {error && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                    {error}
                  </div>
                )}

                <form onSubmit={handleFactCheck} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">
                      {t('dashboard.factCheck.textLabel') || 'Text to Verify'} <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={factCheckText}
                      onChange={(e) => setFactCheckText(e.target.value)}
                      placeholder={t('dashboard.factCheck.placeholder') || 'Enter the text or claim you want to fact-check...'}
                      rows={6}
                      required
                      className="w-full px-4 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 focus:ring-2 focus:ring-shayek-primary transition-colors"
                    />
                    <p className="mt-1 text-xs text-shayek-light-500 dark:text-shayek-dark-500">{factCheckText.length}/10,000 {t('common.characters') || 'characters'}</p>
                  </div>

                  <button
                    type="submit"
                    disabled={factCheckLoading || !factCheckText.trim()}
                    className="w-full bg-shayek-primary dark:bg-shayek-primary-600 text-white py-2 px-4 rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-shayek"
                  >
                    {factCheckLoading ? (t('dashboard.factCheck.checking') || 'Checking...') : (t('dashboard.factCheck.checkButton') || 'Check Facts')}
                  </button>
                </form>

                {factCheckResult && (
                  <div className="mt-6 p-4 rounded-lg border border-shayek-light-300 dark:border-shayek-dark-200 bg-shayek-light-50 dark:bg-shayek-dark-50">
                    <h3 className="font-semibold mb-3 text-gray-900 dark:text-gray-100">
                      {t('dashboard.factCheck.matchingSources', { defaultValue: 'Related articles' })}
                    </h3>
                    {factCheckResult.matches && factCheckResult.matches.length > 0 ? (
                      <div className="space-y-2">
                        {factCheckResult.matches.slice(0, 5).map((match, idx) => (
                          <div key={idx} className="p-3 bg-white dark:bg-shayek-dark-100 rounded border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
                            <a 
                              href={match.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-shayek-primary hover:text-shayek-primary-600 dark:text-shayek-red-light dark:hover:text-shayek-red-light font-medium transition-colors"
                            >
                              {match.title}
                            </a>
                            <p className="text-xs text-shayek-light-500 dark:text-shayek-dark-500 mt-1">
                              {match.source}{match.published_at ? ` • ${new Date(match.published_at).toLocaleDateString()}` : ''}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {t('dashboard.factCheck.noMatches', { defaultValue: 'No related articles found.' })}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar - Your Profile, then Quick Info (when hate-speech has results), then Quick Guide */}
          <div className="lg:col-span-1 space-y-4">
            {/* Your Profile - always first */}
            <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
              <h3 className="font-semibold mb-5 text-shayek-light-900 dark:text-shayek-dark-900 text-lg">Your Profile</h3>
              {userProfile ? (
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    {userProfile.avatar_url ? (
                      <img
                        src={userProfile.avatar_url}
                        alt={userProfile.name || userProfile.email}
                        className="w-16 h-16 rounded-full object-cover border-2 border-shayek-light-200 dark:border-shayek-dark-300"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-shayek-primary to-shayek-primary-600 flex items-center justify-center text-white text-xl font-bold border-2 border-shayek-light-200 dark:border-shayek-dark-300">
                        {(userProfile.name || userProfile.email || 'U').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-shayek-light-900 dark:text-shayek-dark-900 truncate">
                        {userProfile.name || 'No Name Set'}
                      </p>
                      <p className="text-sm text-shayek-light-600 dark:text-shayek-dark-600 truncate">
                        {userProfile.email}
                      </p>
                      <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-semibold rounded-full text-white ${
                        userProfile.role === 'admin' ? 'bg-red-600' :
                        userProfile.role === 'journalist' ? 'bg-blue-600' : 'bg-gray-600'
                      }`}>
                        {userProfile.role === 'admin' ? 'Admin' :
                         userProfile.role === 'journalist' ? 'Team Chayyek' : 'User'}
                      </span>
                    </div>
                  </div>
                  {userProfile.bio && (
                    <p className="text-sm text-shayek-light-700 dark:text-shayek-dark-700 line-clamp-2">
                      {userProfile.bio}
                    </p>
                  )}
                  <div className="pt-4 border-t border-shayek-light-300 dark:border-shayek-dark-200">
                    <Link
                      to="/profile"
                      className="w-full block text-center px-4 py-2 bg-shayek-primary dark:bg-shayek-primary-600 text-white rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-light focus:outline-none focus:ring-2 focus:ring-shayek-primary focus:ring-offset-2 transition-colors text-sm font-medium"
                    >
                      View Full Profile →
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-shayek-primary to-shayek-primary-600 flex items-center justify-center text-white text-xl font-bold mx-auto mb-4">
                    {(user?.email || 'U').charAt(0).toUpperCase()}
                  </div>
                  <p className="text-shayek-light-600 dark:text-shayek-dark-600 mb-4">
                    {user?.email}
                  </p>
                  <Link
                    to="/profile"
                    className="inline-block px-4 py-2 bg-shayek-primary dark:bg-shayek-primary-600 text-white rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-light focus:outline-none focus:ring-2 focus:ring-shayek-primary focus:ring-offset-2 transition-colors text-sm font-medium"
                  >
                    Complete Your Profile →
                  </Link>
                </div>
              )}
            </div>

            {/* Quick Info - only under Profile and above Guide when current tool has a result */}
            {((activeTab === 'hate-speech' && chatMessages.some(m => m.type === 'ai' && m.result)) ||
              (activeTab === 'ai-detection' && aiDetectionResult) ||
              (activeTab === 'fact-check' && factCheckResult)) && (
            <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
              <h3 className="font-semibold mb-5 text-shayek-light-900 dark:text-shayek-dark-900 text-lg">Quick Info</h3>
              <div className="space-y-4">
                {/* Hate Speech: last result only */}
                {activeTab === 'hate-speech' && (() => {
                  const lastResult = chatMessages.filter(m => m.type === 'ai' && m.result).slice(-1)[0]?.result;
                  if (!lastResult) return null;
                  const confidencePercent = (lastResult.confidence * 100).toFixed(0);
                    const confidenceColor = lastResult.confidence >= 0.8 ? 'bg-red-500' :
                                            lastResult.confidence >= 0.6 ? 'bg-orange-500' : 'bg-yellow-500';
                    return (
                      <>
                        <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-shayek-dark-200">
                          <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                            lastResult.is_hate_speech ? 'bg-red-100 dark:bg-red-900/30' : 'bg-green-100 dark:bg-green-900/30'
                          }`}>
                            {lastResult.is_hate_speech ? (
                              <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                            ) : (
                              <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Status</div>
                            <div className={`font-semibold ${lastResult.is_hate_speech ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                              {lastResult.is_hate_speech ? 'Hate Speech Detected' : 'No Hate Speech'}
                            </div>
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-xs text-gray-500 dark:text-gray-400">Confidence Level</div>
                            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{confidencePercent}%</div>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-shayek-dark-200 rounded-full h-2.5 overflow-hidden">
                            <div className={`h-full ${confidenceColor} transition-all duration-300`} style={{ width: `${confidencePercent}%` }}></div>
                          </div>
                        </div>
                        {lastResult.severity && (
                          <div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Severity</div>
                            <span className={`inline-block px-2.5 py-1 rounded-md text-xs font-medium ${
                              lastResult.severity === 'high' || lastResult.severity === 'critical'
                                ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
                                : lastResult.severity === 'medium'
                                ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-800'
                                : 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
                            }`}>
                              {String(lastResult.severity).charAt(0).toUpperCase() + String(lastResult.severity).slice(1)}
                            </span>
                          </div>
                        )}
                        {lastResult.categories && lastResult.categories.length > 0 && (
                          <div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">Detected Categories</div>
                            <div className="flex flex-wrap gap-2">
                              {lastResult.categories.map((cat, idx) => (
                                <span key={idx} className="px-2.5 py-1 bg-shayek-primary/10 dark:bg-shayek-primary/20 text-shayek-primary dark:text-shayek-red-light rounded-md text-xs font-medium border border-shayek-primary/20 dark:border-shayek-primary/30">
                                  {t(`report.categories.${cat}`) || cat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {lastResult.keywords && lastResult.keywords.length > 0 && (
                          <div className="pt-3 border-t border-gray-200 dark:border-shayek-dark-200">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">Trigger Words Detected</div>
                            <div className="flex flex-wrap gap-1.5">
                              {lastResult.keywords.map((kw, idx) => (
                                <span key={idx} className="px-2 py-1 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-md text-xs font-medium border border-red-200 dark:border-red-800">{kw}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {lastResult.detected_patterns && lastResult.detected_patterns.length > 0 && (
                          <div className="pt-3 border-t border-gray-200 dark:border-shayek-dark-200">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('common.detectedPatterns') || 'Detected Patterns'}</div>
                            <div className="flex flex-wrap gap-1.5">
                              {lastResult.detected_patterns.map((p, idx) => (
                                <span key={idx} className="px-2.5 py-1 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 rounded-md text-xs font-medium border border-purple-200 dark:border-purple-800">{p}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {lastResult.references && lastResult.references.length > 0 && (
                          <div className="pt-3 border-t border-gray-200 dark:border-shayek-dark-200">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('common.references') || 'References'}</div>
                            <ul className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                              {lastResult.references.map((ref, idx) => (
                                <li key={idx}>
                                  {ref.url ? (
                                    <a href={ref.url} target="_blank" rel="noopener noreferrer" className="text-sm text-shayek-primary dark:text-shayek-red-light hover:underline block">
                                      {ref.source || ref.title || ref.url}
                                    </a>
                                  ) : (
                                    <span className="text-sm text-gray-700 dark:text-gray-300 block">{ref.source || ref.title || '—'}</span>
                                  )}
                                  {ref.note && <span className="text-xs text-gray-500 dark:text-gray-400 block ml-0">{ref.note}</span>}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </>
                    );
                  })()}

                {/* AI Detection: last result only */}
                {activeTab === 'ai-detection' && aiDetectionResult && (() => {
                  const confidencePct = aiDetectionResult.confidence != null ? Math.round(aiDetectionResult.confidence * 100) : 0;
                  const isManipulated = aiDetectionResult.is_ai_manipulated === true;
                  const isUncertain = aiDetectionResult.is_uncertain === true;
                  const isRed = isManipulated;
                  const isAmber = !isManipulated && isUncertain;
                  const isGreen = !isManipulated && !isUncertain;
                  const iconBg = isRed ? 'bg-red-100 dark:bg-red-900/30' : isAmber ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-green-100 dark:bg-green-900/30';
                  const statusClass = isRed ? 'text-red-600 dark:text-red-400' : isAmber ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400';
                  const statusText = isRed ? (t('dashboard.deepfake.mightBeManipulated') || 'This might be AI manipulated or generated.') : isAmber ? (t('dashboard.deepfake.inconclusive') || 'Result inconclusive.') : (t('dashboard.deepfake.notManipulatedPercent', { percent: confidencePct }) || `We are ${confidencePct}% sure this is not manipulated.`);
                  return (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-shayek-dark-200">
                        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${iconBg}`}>
                          {isRed ? (
                            <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          ) : isAmber ? (
                            <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Status</div>
                          <div className={`font-semibold ${statusClass}`}>
                            {statusText}
                          </div>
                          {isAmber && (t('dashboard.deepfake.inconclusiveRecommendation') || 'We recommend manual review.') && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{t('dashboard.deepfake.inconclusiveRecommendation') || 'We recommend manual review.'}</div>
                          )}
                        </div>
                      </div>
                      {aiDetectionResult.confidence != null && (
                        <div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Confidence</div>
                          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{confidencePct}%</div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Fact Check: last result only */}
                {activeTab === 'fact-check' && factCheckResult && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-shayek-dark-200">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-shayek-primary/10 dark:bg-shayek-primary/20">
                          <svg className="w-5 h-5 text-shayek-primary dark:text-shayek-red-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Fact Check</div>
                          <div className="font-semibold text-shayek-light-900 dark:text-shayek-dark-900">
                            {factCheckResult.matches && factCheckResult.matches.length > 0
                              ? t('dashboard.quickInfo.matchesFound', '{{count}} related article(s) found', { count: factCheckResult.matches.length })
                              : t('dashboard.factCheck.noMatches', 'No related articles found')}
                          </div>
                        </div>
                      </div>
                      {factCheckResult.matches && factCheckResult.matches.length > 0 && (
                        <ul className="space-y-1.5 text-xs">
                          {factCheckResult.matches.slice(0, 3).map((match, idx) => (
                            <li key={idx}>
                              <a href={match.url} target="_blank" rel="noopener noreferrer" className="text-shayek-primary dark:text-shayek-red-light hover:underline block truncate">
                                {match.title}
                              </a>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                )}
              </div>
            </div>
            )}

            {/* Quick Guide for current tool - below Your Profile / Quick Info */}
            <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-4 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
              <h3 className="font-semibold mb-3 text-shayek-light-900 dark:text-shayek-dark-900 text-sm">
                {t('dashboard.quickGuide.title', 'Quick Guide')}
              </h3>
              {activeTab === 'hate-speech' && (
                <div className="text-xs text-shayek-light-600 dark:text-shayek-dark-600 space-y-2">
                  <p>{t('dashboard.quickGuide.hateSpeech', 'Paste or type text and click Check Text. The AI will analyze for hate speech and show confidence, categories, and trigger words. Use "Why we concluded this" for reasoning and linked sources.')}</p>
                </div>
              )}
              {activeTab === 'ai-detection' && (
                <div className="text-xs text-shayek-light-600 dark:text-shayek-dark-600 space-y-2">
                  <p>{t('dashboard.quickGuide.deepfake', 'Upload an image or video. Chayyek will analyze it for signs of manipulation or AI-generated content. Results show whether the media appears authentic or manipulated. Limitations: Allowed formats — images: JPEG, PNG, GIF, WebP; videos: MP4, MOV, AVI; PDF. Maximum file size: 50 MB.')}</p>
                </div>
              )}
              {activeTab === 'fact-check' && (
                <div className="text-xs text-shayek-light-600 dark:text-shayek-dark-600 space-y-2">
                  <p>{t('dashboard.quickGuide.factCheck', 'Enter the text or claim you want to verify. The system searches news and trusted sources and returns related articles. Use matches to verify claims.')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <DisclaimerFooter />
    </div>
  );
};

export default Dashboard;
